import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { Copy, KeyRound, Plus, Trash2, CheckCircle2 } from 'lucide-react';

/**
 * Personal access tokens for the CRM MCP endpoint (functions/mcpServer.js).
 * Tokens are issued/listed/revoked through the mcpManageTokens callable; the
 * raw token is shown exactly once at creation and only its hash is stored.
 */

const MCP_URL = 'https://us-central1-bba-portal-894c2.cloudfunctions.net/mcpServer';

const manageTokens = httpsCallable(functions, 'mcpManageTokens');

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const McpTokensSection = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [newToken, setNewToken] = useState(null); // { token, label }
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await manageTokens({ action: 'list' });
      const rows = res.data?.tokens || [];
      rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setTokens(rows);
      setError('');
    } catch (err) {
      console.error('Failed to load MCP tokens:', err);
      setError('Could not load your access tokens.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleIssue = async () => {
    setIssuing(true);
    setError('');
    try {
      const res = await manageTokens({ action: 'issue', label: label.trim() || 'Claude' });
      setNewToken({ token: res.data?.token, label: res.data?.label });
      setLabel('');
      setCopied(false);
      await refresh();
    } catch (err) {
      console.error('Failed to issue MCP token:', err);
      setError(err.message || 'Could not create a token.');
    } finally {
      setIssuing(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    if (!window.confirm('Revoke this token? Anything using it will lose access immediately.')) return;
    setRevokingId(tokenId);
    try {
      await manageTokens({ action: 'revoke', tokenId });
      await refresh();
    } catch (err) {
      console.error('Failed to revoke MCP token:', err);
      setError(err.message || 'Could not revoke the token.');
    } finally {
      setRevokingId(null);
    }
  };

  const copyText = async (text, done) => {
    try {
      await navigator.clipboard.writeText(text);
      done(true);
      setTimeout(() => done(false), 2000);
    } catch {
      // Clipboard can be unavailable in some contexts; the value stays visible to copy manually.
    }
  };

  const active = tokens.filter((t) => !t.revoked);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 mb-6">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
        <span className="text-lg leading-none">🤖</span>
        Claude / MCP Access
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Connect Claude (or any MCP client) to the CRM. Server URL:
        <span className="ml-2 inline-flex items-center gap-1.5 align-middle">
          <code className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 break-all">
            {MCP_URL}
          </code>
          <button
            type="button"
            onClick={() => copyText(MCP_URL, setCopiedUrl)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Copy MCP server URL"
          >
            {copiedUrl ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </span>
      </p>

      {/* One-time reveal for a freshly minted token */}
      {newToken?.token && (
        <div className="mb-4 p-4 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20">
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-1">
            Token created — copy it now. It cannot be shown again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-800 text-gray-800 dark:text-gray-200 break-all">
              {newToken.token}
            </code>
            <button
              type="button"
              onClick={() => copyText(newToken.token, setCopied)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shrink-0"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-2">
            Use it as the Bearer token / Authorization header when adding the MCP server in Claude.
          </p>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
      )}

      {/* Issue a new token */}
      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Token label (e.g. Claude Desktop)"
          maxLength={100}
          className="flex-1 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={handleIssue}
          disabled={issuing}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          {issuing ? 'Creating…' : 'New token'}
        </button>
      </div>

      {/* Existing tokens */}
      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading tokens…</p>
      ) : active.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No active tokens yet.</p>
      ) : (
        <div className="space-y-2">
          {active.map((t) => (
            <div
              key={t.tokenId}
              className="flex items-center justify-between gap-4 p-3 rounded-xl border border-gray-200 dark:border-gray-800"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                  <KeyRound className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {t.label || 'Untitled'}
                    {t.suffix && (
                      <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">…{t.suffix}</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Created {formatDate(t.createdAt)} · Last used {formatDate(t.lastUsedAt)}
                    {t.expiresAt ? ` · Expires ${formatDate(t.expiresAt)}` : ''}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(t.tokenId)}
                disabled={revokingId === t.tokenId}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {revokingId === t.tokenId ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default McpTokensSection;
